import { Button } from "reactstrap";
import { IPublicClientApplication } from "@azure/msal-browser";
import {
  useIsAuthenticated,
  useMsal,
} from "@azure/msal-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authorize } from "../../api/Auth/authorization";
import { useUser } from "../../contexts/userContextApi";

// Reuse an authorization request if it is already in progress.
// This prevents concurrent duplicate API calls in React Strict Mode.
const authorizationRequests = new Map<string, Promise<any>>();

const authorizeOnce = (token: string): Promise<any> => {
  const existingRequest = authorizationRequests.get(token);

  if (existingRequest) {
    return existingRequest;
  }

  const request = authorize(token).finally(() => {
    authorizationRequests.delete(token);
  });

  authorizationRequests.set(token, request);

  return request;
};

export default function UnAuth() {
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();

  const {
    setIsAuthorize,
    setIsLoadingLandingPage,
    setRole,
  } = useUser();

  const [isUnAuthorizePageLoaded, setIsUnAuthorizePageLoaded] =
    useState(false);

  const [redirectTimer, setRedirectTimer] = useState(20);
  const [startCountdown, setStartCountdown] = useState(false);
  const [sessionExpiry, setSessionExpiry] = useState(false);

  // Clear session and start the unauthorized countdown.
  const clearSessionAndRedirect = () => {
    sessionStorage.clear();
    setRedirectTimer(20);
    setStartCountdown(true);
  };

  // Countdown timer.
  useEffect(() => {
    if (!startCountdown || redirectTimer === 0) {
      return;
    }

    const timer = window.setTimeout(() => {
      setRedirectTimer((previous) => previous - 1);
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [redirectTimer, startCountdown]);

  // Logout when the countdown finishes.
  useEffect(() => {
    if (startCountdown && redirectTimer === 0) {
      redirectSignOut(instance);
    }
  }, [redirectTimer, startCountdown, instance]);

  // Authenticate and establish the new application session.
  useEffect(() => {
    let active = true;

    const handleAuthorization = async () => {
      if (!isAuthenticated) {
        navigate("/");
        return;
      }

      const account = accounts[0];

      if (!account?.idToken) {
        navigate("/");
        return;
      }

      try {
        // Clear the old application session before authorizing.
        //sessionStorage.clear();

        setIsLoadingLandingPage(true);
        setIsUnAuthorizePageLoaded(false);

        // Duplicate concurrent calls reuse the same promise.
        //const idTokens='eyJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiIsImtpZCI6ImRndlNEdks4QTVLeUt5cHB3MWRBd1RYRDNDQSJ9.eyJhdWQiOiI5ZTBiZDdkNy04YmU0LTRlNzMtYWQ5OS04MWMyN2YzMTA4YjYiLCJpc3MiOiJodHRwczovL2xvZ2luLm1pY3Jvc29mdG9ubGluZS5jb20vZWE4MDk1MmUtYTQ3Ni00MmQ0LWFhZjQtNTQ1Nzg1MmIwZjdlL3YyLjAiLCJpYXQiOjE3OTA2MDAwNjUsIm5iZiI6MTc5MDYwMDA2NSwiZXhwIjoxNzkwNjAzOTY1LCJhaW8iOiJBY1FBTy84ZUFBQUE4WUxYZ2xqd1NjSEpjakllc1FvL2lZcDVjNitvUDlLNFZHRjZxQXBuenp6WGdEcndFS0RPT3YzZUpGMk1GZjk4RTgzMHJZdmtIYVRUZGluWDZwVmh3aERHRVowdG8xYnJNa1A5RWJiMmxHZmRaYlpCbVQvTXRydGduK0tmS2F4TXhpMWRnTnJnWlY0WVk4QmhTU2VJOFdPOHJaMDQyOHdoSXpYcjNvYmlnSG9TSXdXYVVvSDlYVGVJQllqUVJWaGMyN2prRFFuYjFSU3BRVzZJL3JNTGNLY0FlSGRzMWg1bnJVeElnZ0M1LzdkVlVhTHU1VEFwYVd0eUYxeHgwR2FYIiwibmFtZSI6IlN1Z2l1cmEsIEF0c3VzaGkiLCJub25jZSI6IjAxYTBlODE5LTI2N2YtNzFjZi1hZjY2LTMxNTZjMWYwNjJmNSIsIm9pZCI6ImE0ZTgxZDNkLWQ5MjUtNDYyMy1iMTNkLWY4YTAzYmZjNWQ3MiIsInByZWZlcnJlZF91c2VybmFtZSI6ImF0c3VzaGkuc3VnaXVyYUBjYXN0cm9sLmNvbSIsInJoIjoiMS5BUWtBTHBXQTZuYWsxRUtxOUZSWGhTc1BmdGZYQzU3a2kzTk9yWm1Cd244eENMWUpBQTRKQUEuIiwicm9sZXMiOlsiR01FREhQUk9EVVNFUkxJU1RAY2FzdHJvbC5jb20iXSwic2lkIjoiMDA3ZWQ0ZGEtYTc2MC1hMjYzLTFmOTMtN2QzMmRhYjY4ZmI0Iiwic3ViIjoicVJnZXphUl85WHUybUxhMHlwTENSbzRKTGR1Q0JhN0V3aDduYkY2UmVNVSIsInRpZCI6ImVhODA5NTJlLWE0NzYtNDJkNC1hYWY0LTU0NTc4NTJiMGY3ZSIsInV0aSI6IkdZdlJfT1VCYUVXYXhsQ3dHemwwQUEiLCJ2ZXIiOiIyLjAiLCJOVElEIjoic3VhaDZwIn0.mGVdNWxjk9gJwHps2iElOjomo8fjrhK-VaspyXzz_YHJ77qZST-U9E1WW8E5uBTJYUIdaTh3Q0yaxuinmjajJ_t-G3hCzGGT0xwdwFkgl6SN4au5fVPLZlOUsN9SQRyuQVaZMHh1vDtoCjBGmnO9R4sZjsFxh1wMs_BwyOrUgUJE_7oHn8EUeEdoAzkVRdzm9YJyYrLUi_Oq60oVdXCGG-1LdYmxh4Pa84LIvLF9chyXGUJclQmgGq17FWUaloqBCAn57oNvD5lwvXoEpmDZijHCNlRHn2OXZ-fLQ3Pjbi8_he3fQqwnalQIhsvkaAsiARJ9wngXqgra1bCO7wBYZg'

        const res = await authorizeOnce(account.idToken);
         //console.log("Authorization response:", res);
        // Ignore stale results after effect cleanup.
        if (!active) return;

        // Handle expired/unauthorized responses.
        if (res?.statusCode === 401) {
          setSessionExpiry(true);
          sessionStorage.setItem("isUnAuth", "false");
          setIsAuthorize(false);
          return;
        }

        if (
          res ===
          "User is not authorized to access this resource with an explicit deny"
        ) {
          sessionStorage.setItem("isUnAuth", "false");
          setIsAuthorize(false);
          clearSessionAndRedirect();
          navigate("/UnAuthorize");
          return;
        }

        // Validate the authorization response before storing the session.
        if (!res?.jwtToken) {
          throw new Error(
            "Authorization response did not contain a JWT token."
          );
        }

        // Store the new application session.
        sessionStorage.setItem("jwt", res.jwtToken);
        sessionStorage.setItem("isAdmin", String(res.role));
        sessionStorage.setItem("isUnAuth", "true");
        const role = res.role;
      sessionStorage.setItem("role", role);
      setRole(role);
      sessionStorage.setItem(
        "tokenExpireTime",
        res.tokenExpireTime
      );
        setIsAuthorize(true);

        // Navigate only after the session has been stored.
        navigate("/drn");
      } catch (error) {
        if (!active) return;

        console.error("Authorization failed:", error);

        setIsAuthorize(false);
        sessionStorage.clear();
        navigate("/UnAuthorize");
      } finally {
        if (active) {
          setIsLoadingLandingPage(false);
          setIsUnAuthorizePageLoaded(true);
        }
      }
    };

    handleAuthorization();

    return () => {
      active = false;
    };
  }, [
    isAuthenticated,
    accounts,
    navigate,
    setIsAuthorize,
    setIsLoadingLandingPage,
  ]);

  // Sign out through MSAL.
  const redirectSignOut = (
    msalInstance: IPublicClientApplication
  ) => {
    localStorage.removeItem("loginCaller");
    sessionStorage.clear();

    msalInstance.logoutRedirect({
      postLogoutRedirectUri:
        "https://dtztk57qeqd06.cloudfront.net/",
    });
  };

  return (
    <>
      {sessionExpiry ? (
        <div
          style={{
            textAlign: "center",
            marginTop: "100px",
          }}
        >
          <h4>Your Token has expired.</h4>

          <div style={{ marginBottom: "20px" }}>
            Please logout this session and login again.
          </div>

          <Button
            color="primary"
            onClick={() => redirectSignOut(instance)}
          >
            Click Here To Logout
          </Button>
        </div>
      ) : (
        isUnAuthorizePageLoaded && (
          <div
            style={{
              textAlign: "center",
              padding: "20px",
            }}
          >
            {startCountdown && (
              <div
                style={{
                  marginTop: "20px",
                  fontSize: "16px",
                  color: "#666",
                }}
              >
                Redirecting to login in{" "}
                <span
                  style={{
                    fontWeight: "bold",
                    color: "#d9534f",
                  }}
                >
                  {redirectTimer}
                </span>{" "}
                seconds...
              </div>
            )}

            <div
              style={{
                marginTop: "15px",
                fontWeight: "bold",
              }}
            >
              Please select the correct login option:
            </div>

            <div style={{ marginTop: "8px" }}>
              If you are a <strong>BP staff member</strong>,
              select the <strong>BP Staff Enter Here</strong>{" "}
              button.
            </div>

            <div style={{ marginTop: "5px" }}>
              <strong>OR</strong>
            </div>

            <div>
              If you are <strong>not a BP staff member</strong>,
              select the <strong>Vendor Login</strong> button.
            </div>

            <div
              style={{
                marginTop: "15px",
                fontWeight: "bold",
              }}
            >
              OR
            </div>

            <div style={{ marginTop: "15px" }}>
              You do not have the necessary permissions.
            </div>

            <div>
              Reach out to your administrator for access.
            </div>
          </div>
        )
      )}
    </>
  );
}

function setRole(role: any) {
  throw new Error("Function not implemented.");
}
